/**
 * CoverageChangeCard - Card for insurance coverage entity changes.
 *
 * Displays carrier, policy type, category, benefits, claims, adjusters,
 * vehicles, policy details, and more.
 */

import { Download, Mail, MapPin, Phone, Printer, Shield } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  BlobLink,
  CarrierDivisionLink,
  CarrierLink,
  PropertyLink,
  TaskLink,
} from '@/components/entity-links/entities';
import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';
import { useLazyDownloadDocumentQuery } from '@/services/api/lettersService';

import { formatDollar, formatEnumValue, formatFieldValue } from '../utils/formatting';
import { useChangedFields } from '../utils/useChangedFields';
import { NestedChangeIndicator } from './ChangeIndicators';
import { CollapsibleSection } from './CollapsibleSection';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';

const CATEGORY_LABELS: Record<string, string> = {
  FIRST_PARTY: '1P',
  FIRST_PARTY_HEALTH: 'Health',
  THIRD_PARTY: '3P',
  NOT_LIABLE_INVOLVED_PARTY: 'Not-Liable',
  PROPERTY: 'Premises',
  UNCLEAR: 'Unclear',
  WORKERS_COMPENSATION: 'WC',
};

const POLICY_TYPE_LABELS: Record<string, string> = {
  AUTO: 'Auto',
  HOMEOWNERS: 'Home',
  COMMERCIAL: 'Commercial',
  HEALTH: 'Health',
  UMBRELLA: 'Umbrella',
  WORKERS_COMPENSATION: 'WC',
  GENERAL_LIABILITY: 'GL',
  OTHER: 'Policy',
};

const BENEFIT_LABELS: Record<string, string> = {
  PIP: 'PIP',
  MEDPAY: 'MedPay',
  UM: 'UM',
  UIM: 'UIM',
  BI: 'BI',
  PD: 'PD',
  COMPREHENSIVE: 'Comprehensive',
  COLLISION: 'Collision',
  WORKERS_COMPENSATION: "Workers' Comp",
  OTHER: 'Other',
};

const KEY_FIELDS: FieldConfig[] = [
  { key: 'liability_status', label: 'Liability', isEnum: true },
  { key: 'category', label: 'Category', isEnum: true },
];

const HANDLE_ICONS: Record<string, React.ElementType> = {
  PHONE: Phone,
  EMAIL: Mail,
  FAX: Printer,
  MAILING_ADDRESS: MapPin,
};

interface CoverageChangeCardProps {
  entity: EntityChangeSchema;
  relatedTasks?: EntityChangeSchema[];
  matterId?: string;
}

export const CoverageChangeCard: React.FC<CoverageChangeCardProps> = ({
  entity,
  relatedTasks = [],
  matterId,
}) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const policyType = data.policy_type as string | undefined;
  const category = data.category as string | undefined;
  const carrier = data.carrier as Record<string, unknown> | undefined;
  const namedInsured = (data.named_insured as string[]) ?? [];
  const policyIdentifiers =
    (data.policy_identifiers as Record<string, unknown>[]) ?? [];
  const benefits = (data.benefits as Record<string, unknown>[]) ?? [];
  const claims = (data.claims as Record<string, unknown>[]) ?? [];
  const adjusters = (data.adjusters as Record<string, unknown>[]) ?? [];
  const vehicles = (data.vehicles as Record<string, unknown>[]) ?? [];
  const policyDetails = (data.policy_details as Record<string, unknown>[]) ?? [];
  const subrogator = data.subrogator as Record<string, unknown> | undefined;
  const [showRelatedWork, setShowRelatedWork] = useState(false);
  const coverageId = entity.entity_id || (data.id as string | undefined) || '';

  const carrierName =
    (carrier?.name as string) || policyType || 'Unknown Coverage';
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  const relatedFileCount = useMemo(
    () =>
      relatedTasks.reduce(
        (count, task) => {
          const taskSnapshot =
            task.change_type === 'deleted' ? task.old_snapshot : task.new_snapshot;
          const taskData = (taskSnapshot?.data ?? {}) as Record<string, unknown>;
          const attachments = (taskData.attachments as Record<string, unknown>[] | undefined) ?? [];
          return count + attachments.length;
        },
        0,
      ),
    [relatedTasks],
  );
  const shouldShowRelatedSection = Boolean(matterId && coverageId);

  let relatedWorkBody: React.ReactNode = null;
  if (showRelatedWork) {
    if (relatedTasks.length === 0) {
      relatedWorkBody = (
        <p className="text-sm text-gray-500">
          No related tasks found for this coverage.
        </p>
      );
    } else {
      relatedWorkBody = (
        <div className="space-y-2">
          {relatedTasks.map((task) => {
            const taskSnapshot =
              task.change_type === 'deleted' ? task.old_snapshot : task.new_snapshot;
            const taskData = (taskSnapshot?.data ?? {}) as Record<string, unknown>;
            const taskId = task.entity_id || (taskData.id as string | undefined) || '';
            const taskName = (taskData.name as string) || 'Task';
            const taskStatus = actions?.statusOverrides[task.id as string] ?? task.status;
            const attachments =
              (taskData.attachments as Record<string, unknown>[] | undefined) ?? [];

            return (
              <div key={task.id} className="rounded-md border border-gray-100 p-2">
                <div className="flex items-center justify-between gap-2">
                  {taskId ? (
                    <TaskLink
                      entityId={taskId}
                      matterId={matterId ?? ''}
                      className="text-sm font-medium"
                    >
                      {taskName}
                    </TaskLink>
                  ) : (
                    <span className="text-sm font-medium text-gray-900">{taskName}</span>
                  )}
                  <Badge color="zinc" className="text-xs">
                    {taskStatus}
                  </Badge>
                </div>

                {attachments.length ? (
                  <div className="mt-1 space-y-1 pl-2">
                    <div className="text-xs text-gray-400">Related Files</div>
                    {attachments.map((attachment, idx) => {
                      const attachmentId = attachment.id as string | undefined;
                      const attachmentName = (attachment.name as string | undefined) || 'File';

                      if (!attachmentId) {
                        return (
                          <div key={`${task.id}-attachment-${idx}`} className="text-sm text-gray-500">
                            {attachmentName}
                          </div>
                        );
                      }

                      return (
                        <div key={attachmentId} className="text-sm">
                          <BlobLink entityId={attachmentId} matterId={matterId ?? ''}>
                            {attachmentName}
                          </BlobLink>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-gray-400">
                    No related files in snapshot
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  }

  const downloadLorLetter = async (documentId: string) => {
    if (!matterId) return;
    const result = await downloadDocument({
      matterId,
      documentInstanceId: documentId,
    });

    if ('data' in result && result.data instanceof Blob) {
      const url = window.URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'LOR.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={carrierName}
      icon={Shield}
      iconBgClassName="bg-emerald-50"
      iconTextClassName="text-emerald-600"
      entityStatus={effectiveStatus}
      onStatusChange={
        actions
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      badges={
        <>
          {policyType && (
            <Badge color="zinc" className="text-xs">
              {POLICY_TYPE_LABELS[policyType] ?? formatEnumValue(policyType)}
            </Badge>
          )}
          {category && (
            <Badge color="zinc" className="text-xs">
              {CATEGORY_LABELS[category] ?? formatEnumValue(category)}
            </Badge>
          )}
        </>
      }
      changeCount={changes.totalChanges}
      hasExpandedContent
    >
      {/* ── Policy & Carrier ────────────────────────────── */}
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* Carrier name + contact inline */}
      {carrier && (
        <div className="space-y-0.5">
          {typeof carrier.name === 'string' && carrier.name && (
            <div className="flex items-center gap-1.5 px-1 py-0.5">
              <span className="text-xs text-gray-400 shrink-0">Carrier:</span>
              <span className="text-sm text-gray-700">{carrier.name}</span>
              <NestedChangeIndicator prefix="carrier.name" changes={changes} />
            </div>
          )}
          {(carrier.handles as Record<string, unknown>[] | undefined)?.map(
            (handle, i) => {
              const handleType = handle.handle_type as string;
              const HandleIcon = HANDLE_ICONS[handleType] ?? Phone;
              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 px-1 py-0.5 text-sm text-gray-700"
                >
                  <HandleIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="whitespace-normal break-words">
                    {(handle.value as string) || ''}
                  </span>
                  <NestedChangeIndicator
                    prefix={`carrier.handles.${i}`}
                    changes={changes}
                  />
                </div>
              );
            },
          )}
          {typeof carrier.address === 'string' && carrier.address && (
            <div className="flex items-center gap-1.5 px-1 py-0.5 text-sm text-gray-700">
              <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              <span className="whitespace-normal break-words">{carrier.address}</span>
              <NestedChangeIndicator prefix="carrier.address" changes={changes} />
            </div>
          )}
        </div>
      )}

      {/* Named insured + policy identifiers inline */}
      {(namedInsured.length > 0 || policyIdentifiers.length > 0) && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
          {namedInsured.map((name, i) => (
            <div
              key={`ni-${i}`}
              className="flex items-center gap-1.5 px-1 py-0.5"
            >
              <span className="text-xs text-gray-400 shrink-0">Insured:</span>
              <span className="text-sm text-gray-700">{name}</span>
              <NestedChangeIndicator
                prefix={`named_insured.${i}`}
                changes={changes}
              />
            </div>
          ))}
          {policyIdentifiers.map((pid, i) => (
            <div
              key={`pid-${i}`}
              className="flex items-center gap-1.5 px-1 py-0.5"
            >
              <span className="text-xs text-gray-400 shrink-0">
                {formatEnumValue(pid.identifier_type as string) ?? 'ID'}:
              </span>
              <span className="text-sm text-gray-700">
                {(pid.identifier_value as string) || '\u2014'}
              </span>
              <NestedChangeIndicator
                prefix={`policy_identifiers.${i}`}
                changes={changes}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── People (Adjusters & Subrogator) ───────────── */}
      {(adjusters.length > 0 || subrogator) && (
        <>
          <div className="border-t border-gray-100" />
          <CollapsibleSection
            title="People"
            count={adjusters.length + (subrogator ? 1 : 0)}
            changeType={entity.change_type}
            hasChanges={
              changes.hasChangesUnder('adjusters') ||
              changes.hasChangesUnder('subrogator')
            }
          >
            {adjusters.map((adjuster, i) => {
              const adjName = adjuster.name as string | undefined;
              const adjBenefitType = adjuster.benefit_type as string | undefined;
              const adjHandles =
                (adjuster.handles as Record<string, unknown>[]) ?? [];
              return (
                <div
                  key={i}
                  className="border border-gray-100 rounded px-3 py-2 space-y-0.5"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                      Adjuster
                    </span>
                    {adjBenefitType && (
                      <Badge color="zinc" className="text-xs">
                        {BENEFIT_LABELS[adjBenefitType] ?? formatEnumValue(adjBenefitType)}
                      </Badge>
                    )}
                    {adjName && (
                      <span className="text-sm text-gray-700">{adjName}</span>
                    )}
                    <NestedChangeIndicator
                      prefix={`adjusters.${i}`}
                      changes={changes}
                    />
                  </div>
                  {adjHandles.map((handle, hi) => {
                    const handleType = handle.handle_type as string;
                    const HandleIcon = HANDLE_ICONS[handleType] ?? Phone;
                    return (
                      <div
                        key={hi}
                        className="flex items-center gap-1.5 text-sm text-gray-600"
                      >
                        <HandleIcon className="h-3 w-3 text-gray-400 shrink-0" />
                        <span className="whitespace-normal break-words">
                          {(handle.value as string) || ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {subrogator && (
              <div className="border border-gray-100 rounded px-3 py-2 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Subrogator
                  </span>
                  {typeof subrogator.name === 'string' && subrogator.name && (
                    <span className="text-sm text-gray-700">
                      {subrogator.name}
                    </span>
                  )}
                  <NestedChangeIndicator
                    prefix="subrogator"
                    changes={changes}
                  />
                </div>
                {(
                  subrogator.handles as Record<string, unknown>[] | undefined
                )?.map((handle, i) => {
                  const handleType = handle.handle_type as string;
                  const HandleIcon = HANDLE_ICONS[handleType] ?? Phone;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 text-sm text-gray-600"
                    >
                      <HandleIcon className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="whitespace-normal break-words">
                        {(handle.value as string) || ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CollapsibleSection>
        </>
      )}

      {/* ── Benefits & Claims ─────────────────────────── */}
      {(benefits.length > 0 || claims.length > 0) && (
        <>
          <div className="border-t border-gray-100" />
          {benefits.length > 0 && (() => {
            const simpleBenefits: { type: string; index: number }[] = [];
            const detailedBenefits: { benefit: Record<string, unknown>; index: number }[] = [];
            benefits.forEach((benefit, i) => {
              const benefitType = benefit.benefit_type as string | undefined;
              const limitations = benefit.limitations as Record<string, unknown> | undefined;
              const hasLimitations = limitations && Object.values(limitations).some((v) => v != null);
              if (hasLimitations) {
                detailedBenefits.push({ benefit, index: i });
              } else {
                simpleBenefits.push({
                  type: benefitType
                    ? (BENEFIT_LABELS[benefitType] ?? formatEnumValue(benefitType))
                    : 'Benefit',
                  index: i,
                });
              }
            });
            return (
              <CollapsibleSection
                title="Benefits"
                count={benefits.length}
                changeType={entity.change_type}
                hasChanges={changes.hasChangesUnder('benefits')}
              >
                {simpleBenefits.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap px-1 py-0.5">
                    {simpleBenefits.map(({ type, index }) => (
                      <span key={index} className="inline-flex items-center gap-1">
                        <Badge color="zinc" className="text-xs">
                          {type}
                        </Badge>
                        <NestedChangeIndicator
                          prefix={`benefits.${index}`}
                          changes={changes}
                        />
                      </span>
                    ))}
                  </div>
                )}
                {detailedBenefits.length > 0 && (
                  <div className="space-y-1.5">
                    {detailedBenefits.map(({ benefit, index }) => {
                      const benefitType = benefit.benefit_type as string | undefined;
                      const limitations = benefit.limitations as Record<string, unknown>;
                      return (
                        <div
                          key={index}
                          className="border border-gray-100 rounded px-3 py-2 space-y-0.5"
                        >
                          <div className="flex items-center gap-2">
                            <Badge color="zinc" className="text-xs">
                              {benefitType
                                ? (BENEFIT_LABELS[benefitType] ??
                                  formatEnumValue(benefitType))
                                : 'Benefit'}
                            </Badge>
                            <NestedChangeIndicator
                              prefix={`benefits.${index}`}
                              changes={changes}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
                            {Object.entries(limitations).map(([key, val]) =>
                              val != null ? (
                                <div
                                  key={key}
                                  className="flex items-center gap-1.5 px-1 py-0.5"
                                >
                                  <span className="text-xs text-gray-400 shrink-0">
                                    {formatEnumValue(key)}:
                                  </span>
                                  <span className="text-sm text-gray-700">
                                    {typeof val === 'number'
                                      ? formatDollar(val)
                                      : formatFieldValue(val)}
                                  </span>
                                </div>
                              ) : null,
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CollapsibleSection>
            );
          })()}
          {claims.length > 0 && (
            <CollapsibleSection
              title="Claims"
              count={claims.length}
              changeType={entity.change_type}
              hasChanges={changes.hasChangesUnder('claims')}
            >
              <div className="space-y-1.5">
                {claims.map((claim, i) => {
                  const claimIdentifier = claim.claim_identifier as string | undefined;
                  const claimBenefitType = claim.benefit_type as string | undefined;
                  const hasContent = claimIdentifier || claimBenefitType;
                  return (
                    <div
                      key={i}
                      className="border border-gray-100 rounded px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        {claimBenefitType && (
                          <Badge color="zinc" className="text-xs">
                            {BENEFIT_LABELS[claimBenefitType] ?? formatEnumValue(claimBenefitType)}
                          </Badge>
                        )}
                        {claimIdentifier ? (
                          <span className="text-sm text-gray-700">
                            #{claimIdentifier}
                          </span>
                        ) : !hasContent && (
                          <span className="text-sm text-gray-400 italic">
                            No claim identifier
                          </span>
                        )}
                        <NestedChangeIndicator
                          prefix={`claims.${i}`}
                          changes={changes}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}
        </>
      )}

      {/* ── Vehicles & Policy Details ─────────────────── */}
      {(vehicles.length > 0 || policyDetails.length > 0) && (
        <>
          <div className="border-t border-gray-100" />
          {vehicles.length > 0 && (
            <CollapsibleSection
              title="Vehicles"
              count={vehicles.length}
              changeType={entity.change_type}
              hasChanges={changes.hasChangesUnder('vehicles')}
            >
              <div className="space-y-1.5">
                {vehicles.map((vehicle, i) => {
                  const year = vehicle.year as string | number | undefined;
                  const make = vehicle.make as string | undefined;
                  const model = vehicle.model as string | undefined;
                  const vin = vehicle.vin as string | undefined;
                  const label = [year, make, model].filter(Boolean).join(' ');
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-1 py-0.5"
                    >
                      <span className="text-sm text-gray-700">
                        {label || 'Vehicle'}
                      </span>
                      {vin && (
                        <span className="text-xs text-gray-400">
                          VIN: {vin}
                        </span>
                      )}
                      <NestedChangeIndicator
                        prefix={`vehicles.${i}`}
                        changes={changes}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}
          {policyDetails.length > 0 && (() => {
            const nonEmptyDetails = policyDetails.filter((detail) => {
              const detailKey = detail.key as string | undefined;
              const detailValue = detail.value as string | undefined;
              return detailKey || detailValue;
            });
            if (nonEmptyDetails.length === 0 && !changes.hasChangesUnder('policy_details')) return null;
            return (
              <CollapsibleSection
                title="Policy Details"
                count={nonEmptyDetails.length}
                changeType={entity.change_type}
                hasChanges={changes.hasChangesUnder('policy_details')}
              >
                {nonEmptyDetails.length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {nonEmptyDetails.map((detail, i) => {
                      const detailKey = detail.key as string | undefined;
                      const detailValue = detail.value as string | undefined;
                      const originalIndex = policyDetails.indexOf(detail);
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-1.5 px-1 py-0.5"
                        >
                          <span className="text-xs text-gray-400 shrink-0">
                            {detailKey || 'Detail'}:
                          </span>
                          <span className="text-sm text-gray-700">
                            {detailValue || '\u2014'}
                          </span>
                          <NestedChangeIndicator
                            prefix={`policy_details.${originalIndex}`}
                            changes={changes}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic px-1">Empty policy details</p>
                )}
              </CollapsibleSection>
            );
          })()}
        </>
      )}

      {/* Entity links for references */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {typeof data.carrier_v2_id === 'string' && data.carrier_v2_id && (
          <div className="flex items-baseline gap-1.5 px-1 py-0.5">
            <span className="text-xs text-gray-400 shrink-0">Carrier:</span>
            <CarrierLink entityId={data.carrier_v2_id} className="text-sm font-normal">
              Carrier
            </CarrierLink>
          </div>
        )}
        {typeof data.carrier_division_id === 'string' && data.carrier_division_id && (
          <div className="flex items-baseline gap-1.5 px-1 py-0.5">
            <span className="text-xs text-gray-400 shrink-0">Division:</span>
            <CarrierDivisionLink entityId={data.carrier_division_id} className="text-sm font-normal">
              Division
            </CarrierDivisionLink>
          </div>
        )}
        {typeof data.property_id === 'string' && data.property_id && matterId && (
          <div className="flex items-baseline gap-1.5 px-1 py-0.5">
            <span className="text-xs text-gray-400 shrink-0">Property:</span>
            <PropertyLink entityId={`${matterId}:${data.property_id}`} className="text-sm font-normal">
              Property
            </PropertyLink>
          </div>
        )}
        {typeof data.lor_document_id === 'string' && data.lor_document_id && matterId && (
          <div className="flex items-center gap-1.5 px-1 py-0.5">
            <span className="text-xs text-gray-400 shrink-0">LOR Document:</span>
            <button
              type="button"
              onClick={() => downloadLorLetter(data.lor_document_id as string)}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
              title="Download LOR letter"
            >
              <Download className="h-3.5 w-3.5" />
              Download LOR
            </button>
          </div>
        )}
      </div>

      {/* Related tasks/files (Entity -> Task -> Related Files) */}
      {shouldShowRelatedSection && (
        <>
          <div className="border-t border-gray-100" />
          <button
            type="button"
            onClick={() => setShowRelatedWork((prev) => !prev)}
            className="flex items-center justify-between gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors w-full text-left py-1"
          >
            <span>
              {showRelatedWork ? 'Hide related tasks/files' : 'Show related tasks/files'}
            </span>
            {showRelatedWork && (
              <span className="text-gray-400">
                ({relatedTasks.length} tasks, {relatedFileCount} files)
              </span>
            )}
          </button>
          {showRelatedWork && <div className="mt-2 space-y-2 pl-1">{relatedWorkBody}</div>}
        </>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        <KeyFieldsGridUnchanged
          fields={KEY_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
    </EntityChangeCardShell>
  );
};
